import { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "utils/@reduxjs/toolkit";
import { AddOrderForm } from "app/containers/CommonModal/types";
import {
  getMockVehicleDetails,
  MOCK_ADD_ON_FORM_VALUES,
  MOCK_ADD_ON_TABLES,
  MOCK_ADDITIONAL_EQUIPMENT,
  MOCK_BOX_FORM_VALUES,
  MOCK_CENTRE_OF_GRAVITY_FORM,
  MOCK_CHASSIS_LOADS,
  MOCK_DIAGRAM_PAGES,
  MOCK_LOAD_DISTRIBUTION_FORM_VALUES,
  MOCK_QUOTATION_BOX_STRUCTURE,
  MOCK_QUOTATION_FORM_VALUES,
  MOCK_QUOTATION_LOCKS,
  MOCK_QUOTATION_ROOF_BOX_STRUCTURE,
  MOCK_QUOTATION_CLOSED_BOX_STRUCTURE,
  MOCK_QUOTATION_CLOSED_BOX_ACCESSORIES,
  MOCK_QUOTATION_INSULATED_BOX_STRUCTURE,
  MOCK_QUOTATION_INSULATED_BOX_ACCESSORIES,
  MOCK_QUOTATION_ROOF_BOX_ACCESSORIES,
  MOCK_QUOTATION_SAFETY_VEHICLE,
  MOCK_QUOTATION_STANDARD_95,
  MOCK_QUOTATION_TYING_EQUIPMENT,
  MOCK_VEHICLE_FORM_VALUES,
  MOCK_VEHICLE_MEASURES,
  MOCK_WEIGHT_AND_WALL_FORM_VALUES,
} from "../components/WorkOrderDetails/data";
import {
  AddOnDetailsState,
  AddOnFormValues,
  AddOnItemFormValue,
  AddOnTableKey,
  AddOnTableRow,
  AddOnTyingStripModalState,
  TableRowModalState,
  AdditionalEquipmentCode,
  AdditionalEquipmentState,
  BoxDetailsFormValues,
  CentreOfGravityAdjustmentMode,
  CentreOfGravityFormValues,
  ChasisLoad,
  ChassisLoadsState,
  VehicleMeasureCode,
  VehicleMeasuresState,
  DiagramDepartmentTab,
  DiagramViewState,
  QuotationBoxStructureState,
  QuotationEquipmentTableKey,
  QuotationLocksState,
  QuotationSafetyVehicleState,
  QuotationStandard95State,
  QuotationTyingEquipmentCardKey,
  QuotationTyingEquipmentState,
  QuotationFrontWallFields,
  QuotationFormValues,
  QuotationRoofBoxCardKey,
  QuotationClosedBoxCardKey,
  QuotationClosedBoxAccessoryCardKey,
  ClosedBoxAccessoryCard,
  QuotationInsulatedBoxCardKey,
  InsulatedBoxStructureCard,
  QuotationInsulatedBoxAccessoryCardKey,
  InsulatedBoxAccessoryCard,
  QuotationRoofBoxAccessoryCardKey,
  RoofBoxAccessoryCard,
  VehicleDetailsFormValues,
  WeightAndWallFormValues,
  LoadDistributionFormValues,
  WorkOrderDetailTab,
  WorkOrderStatus,
  WorkOrderStatusFilter,
  WORK_ORDER_STATUS_VALUES,
  WorkOrderTableRow,
  WorkOrderVehicleDetails,
  AxleStructureEnum,
  BoxTypeOption,
} from "../types";
import { initialDiagramView, initialState } from "./initialState";

const clone = <T>(value: T): T => structuredClone(value);

const createInitialTyingStripModal = (): AddOnTyingStripModalState => ({
  open: false,
  titleKey: "",
  height: "",
  note: "",
});

const createRowModal = (): TableRowModalState => ({
  open: false,
  tableKey: null,
  titleKey: "",
  location: "",
  itemType: "",
  quantity: "1",
  note: "",
});

const ensureAddOnDetails = (state: typeof initialState): AddOnDetailsState => {
  if (!state.addOnDetails) {
    state.addOnDetails = {
      form: clone(MOCK_ADD_ON_FORM_VALUES),
      tables: clone(MOCK_ADD_ON_TABLES),
      tyingStripModal: createInitialTyingStripModal(),
      rowModal: createRowModal(),
    };
  }

  return state.addOnDetails;
};

const createEmptyAddOnTableRow = (): AddOnTableRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  location: "",
  itemType: "",
  quantity: "",
  note: "",
  height: "",
});

const applyStatusFilter = (
  prev: WorkOrderStatusFilter[],
  next: WorkOrderStatusFilter[]
): WorkOrderStatusFilter[] => {
  const prevHasAll = prev.includes("ALL");
  const nextHasAll = next.includes("ALL");

  if (prevHasAll !== nextHasAll) {
    return nextHasAll ? ["ALL"] : [];
  }

  const selectedStatuses = next.filter(
    (value): value is WorkOrderStatus => value !== "ALL"
  );
  const allSelected = WORK_ORDER_STATUS_VALUES.every((status) =>
    selectedStatuses.includes(status)
  );

  return allSelected ? ["ALL"] : selectedStatuses;
};

export const workOrderSlice = createSlice({
  name: "workOrderState",
  initialState,
  reducers: {
    setStatusFilter: (state, action: PayloadAction<WorkOrderStatusFilter[]>) => {
      state.statusFilter = applyStatusFilter(
        state.statusFilter,
        action.payload
      );
    },
    getList: (state) => {
      state.loading = true;
      state.error = null;
    },
    getListSuccess: (state, action: PayloadAction<WorkOrderTableRow[]>) => {
      state.loading = false;
      state.workOrders = action.payload;
    },
    getListFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
  getWorkOrderDetails: (state, action: PayloadAction<number>) => {
      state.detailLoading = false;
      state.error = null;
      state.selectedWorkOrderId = action.payload;
      state.vehicleDetails = getMockVehicleDetails(action.payload);
      state.loadDistributionForm = clone(MOCK_LOAD_DISTRIBUTION_FORM_VALUES);
      state.diagramView = {
        activeDepartment: DiagramDepartmentTab.DIAGRAM,
        activePage: 0,
        pages: clone(MOCK_DIAGRAM_PAGES),
      };
    },
    getWorkOrderDetailsSuccess: (
      state,
      action: PayloadAction<{
        vehicleDetails: WorkOrderVehicleDetails;
        vehicleDetailsForm: VehicleDetailsFormValues;
        boxDetailsForm: BoxDetailsFormValues;
        addOnDetails: AddOnDetailsState;
        diagramView: DiagramViewState;
      }>
    ) => {
      state.detailLoading = false;
      state.vehicleDetails = action.payload.vehicleDetails;
      state.vehicleDetailsForm = action.payload.vehicleDetailsForm;
      state.boxDetailsForm = action.payload.boxDetailsForm;
      state.addOnDetails = action.payload.addOnDetails;
      state.diagramView = action.payload.diagramView;
    },
    getWorkOrderDetailsFailure: (state, action: PayloadAction<string>) => {
      state.detailLoading = false;
      state.error = action.payload;
    },
    clearWorkOrderDetails: (state) => {
      state.selectedWorkOrderId = null;
      state.vehicleDetails = null;
      state.vehicleDetailsForm = null;
      state.boxDetailsForm = null;
      state.weightAndWallForm = null;
      state.loadDistributionForm = null;
      state.addOnDetails = null;
      state.quotationForm = null;
      state.quotationBoxStructure = null;
      state.quotationRoofBoxStructure = null;
      state.quotationClosedBoxStructure = null;
      state.quotationClosedBoxAccessories = null;
      state.quotationInsulatedBoxStructure = null;
      state.quotationInsulatedBoxAccessories = null;
      state.quotationRoofBoxAccessories = null;
      state.quotationTyingEquipment = null;
      state.quotationSafetyVehicle = null;
      state.quotationLocks = null;
      state.quotationStandard95 = null;
      state.centreOfGravityForm = null;
      state.chassisLoads = null;
      state.vehicleMeasures = null;
      state.additionalEquipment = null;
      state.diagramView = initialDiagramView;
      state.activeDetailTab = WorkOrderDetailTab.VEHICLE_DETAILS;
      state.detailLoading = false;
    },
    setActiveDetailTab: (state, action: PayloadAction<WorkOrderDetailTab>) => {
      state.activeDetailTab = action.payload;
    },
    updateVehicleDetailsField: (
      state,
      action: PayloadAction<
        | { field: "axleStructure"; value: AxleStructureEnum }
        | { field: "boxType"; value: BoxTypeOption }
      >
    ) => {
      if (!state.vehicleDetails) {
        return;
      }

      if (action.payload.field === "axleStructure") {
        state.vehicleDetails.axleStructure = action.payload.value;
        return;
      }

      state.vehicleDetails.boxType = action.payload.value;
    },
    updateVehicleDetailsFormField: (
      state,
      action: PayloadAction<{
        field: keyof VehicleDetailsFormValues;
        value: string | boolean;
      }>
    ) => {
      if (!state.vehicleDetailsForm) {
        state.vehicleDetailsForm = clone(MOCK_VEHICLE_FORM_VALUES);
      }

      const { field, value } = action.payload;
      state.vehicleDetailsForm[field] = value as never;
    },
    updateBoxDetailsFormField: (
      state,
      action: PayloadAction<{
        field: keyof BoxDetailsFormValues;
        value: string | boolean;
      }>
    ) => {
      if (!state.boxDetailsForm) {
        state.boxDetailsForm = clone(MOCK_BOX_FORM_VALUES);
      }

      const { field, value } = action.payload;
      state.boxDetailsForm[field] = value as never;
    },
    updateWeightAndWallFormField: (
      state,
      action: PayloadAction<{
        field: keyof WeightAndWallFormValues;
        value: string;
      }>
    ) => {
      if (!state.weightAndWallForm) {
        state.weightAndWallForm = clone(MOCK_WEIGHT_AND_WALL_FORM_VALUES);
      }

      const { field, value } = action.payload;
      state.weightAndWallForm[field] = value;
    },
    updateLoadDistributionFormField: (
      state,
      action: PayloadAction<{
        field: keyof LoadDistributionFormValues;
        value: string;
      }>
    ) => {
      if (!state.loadDistributionForm) {
        state.loadDistributionForm = clone(MOCK_LOAD_DISTRIBUTION_FORM_VALUES);
      }

      const { field, value } = action.payload;
      state.loadDistributionForm[field] = value;
    },
    updateQuotationFormField: (
      state,
      action: PayloadAction<{
        field: keyof QuotationFormValues;
        value: string | boolean;
      }>
    ) => {
      if (!state.quotationForm) {
        state.quotationForm = clone(MOCK_QUOTATION_FORM_VALUES);
      }

      const { field, value } = action.payload;
      state.quotationForm[field] = value as never;
    },
    updateQuotationBoxStructure: (
      state,
      action: PayloadAction<{
        key: keyof QuotationBoxStructureState;
        value: Partial<AddOnItemFormValue> | Partial<QuotationFrontWallFields>;
      }>
    ) => {
      if (!state.quotationBoxStructure) {
        state.quotationBoxStructure = clone(MOCK_QUOTATION_BOX_STRUCTURE);
      }

      const { key, value } = action.payload;
      if (key === "frontWall") {
        state.quotationBoxStructure.frontWall = {
          ...state.quotationBoxStructure.frontWall,
          ...(value as Partial<QuotationFrontWallFields>),
        };
        return;
      }

      state.quotationBoxStructure[key] = {
        ...state.quotationBoxStructure[key],
        ...(value as Partial<AddOnItemFormValue>),
      };
    },
    updateQuotationRoofBoxStructure: (
      state,
      action: PayloadAction<{
        key: QuotationRoofBoxCardKey;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      if (!state.quotationRoofBoxStructure) {
        state.quotationRoofBoxStructure = clone(
          MOCK_QUOTATION_ROOF_BOX_STRUCTURE
        );
      }

      const { key, value } = action.payload;
      state.quotationRoofBoxStructure[key] = {
        ...state.quotationRoofBoxStructure[key],
        ...value,
      };
    },
    updateQuotationClosedBoxStructure: (
      state,
      action: PayloadAction<{
        key: QuotationClosedBoxCardKey;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      if (!state.quotationClosedBoxStructure) {
        state.quotationClosedBoxStructure = clone(
          MOCK_QUOTATION_CLOSED_BOX_STRUCTURE
        );
      }

      const { key, value } = action.payload;
      state.quotationClosedBoxStructure[key] = {
        ...state.quotationClosedBoxStructure[key],
        ...value,
      };
    },
    updateQuotationClosedBoxAccessories: (
      state,
      action: PayloadAction<{
        key: QuotationClosedBoxAccessoryCardKey;
        value: Partial<ClosedBoxAccessoryCard>;
      }>
    ) => {
      if (!state.quotationClosedBoxAccessories) {
        state.quotationClosedBoxAccessories = clone(
          MOCK_QUOTATION_CLOSED_BOX_ACCESSORIES
        );
      }

      const { key, value } = action.payload;
      state.quotationClosedBoxAccessories[key] = {
        ...state.quotationClosedBoxAccessories[key],
        ...value,
      };
    },
    addQuotationClosedBoxTyingStripRow: (state) => {
      if (!state.quotationClosedBoxAccessories) {
        state.quotationClosedBoxAccessories = clone(
          MOCK_QUOTATION_CLOSED_BOX_ACCESSORIES
        );
      }

      state.quotationClosedBoxAccessories.tyingStrips.push(
        createEmptyAddOnTableRow()
      );
    },
    updateQuotationInsulatedBoxStructure: (
      state,
      action: PayloadAction<{
        key: QuotationInsulatedBoxCardKey;
        value: Partial<InsulatedBoxStructureCard>;
      }>
    ) => {
      if (!state.quotationInsulatedBoxStructure) {
        state.quotationInsulatedBoxStructure = clone(
          MOCK_QUOTATION_INSULATED_BOX_STRUCTURE
        );
      }

      const { key, value } = action.payload;
      state.quotationInsulatedBoxStructure[key] = {
        ...state.quotationInsulatedBoxStructure[key],
        ...value,
      };
    },
    updateQuotationInsulatedBoxAccessories: (
      state,
      action: PayloadAction<{
        key: QuotationInsulatedBoxAccessoryCardKey;
        value: Partial<InsulatedBoxAccessoryCard>;
      }>
    ) => {
      if (!state.quotationInsulatedBoxAccessories) {
        state.quotationInsulatedBoxAccessories = clone(
          MOCK_QUOTATION_INSULATED_BOX_ACCESSORIES
        );
      }

      const { key, value } = action.payload;
      state.quotationInsulatedBoxAccessories[key] = {
        ...state.quotationInsulatedBoxAccessories[key],
        ...value,
      };
    },
    addQuotationInsulatedBoxTyingStripRow: (state) => {
      if (!state.quotationInsulatedBoxAccessories) {
        state.quotationInsulatedBoxAccessories = clone(
          MOCK_QUOTATION_INSULATED_BOX_ACCESSORIES
        );
      }

      state.quotationInsulatedBoxAccessories.tyingStrips.push(
        createEmptyAddOnTableRow()
      );
    },
    resetQuotationInsulatedBox: (state) => {
      state.quotationForm = clone(MOCK_QUOTATION_FORM_VALUES);
      state.quotationInsulatedBoxStructure = clone(
        MOCK_QUOTATION_INSULATED_BOX_STRUCTURE
      );
      state.quotationInsulatedBoxAccessories = clone(
        MOCK_QUOTATION_INSULATED_BOX_ACCESSORIES
      );
    },
    updateQuotationRoofBoxAccessories: (
      state,
      action: PayloadAction<{
        key: QuotationRoofBoxAccessoryCardKey;
        value: Partial<RoofBoxAccessoryCard>;
      }>
    ) => {
      if (!state.quotationRoofBoxAccessories) {
        state.quotationRoofBoxAccessories = clone(
          MOCK_QUOTATION_ROOF_BOX_ACCESSORIES
        );
      }

      const { key, value } = action.payload;
      state.quotationRoofBoxAccessories[key] = {
        ...state.quotationRoofBoxAccessories[key],
        ...value,
      };
    },
    updateQuotationTyingEquipment: (
      state,
      action: PayloadAction<{
        key: QuotationTyingEquipmentCardKey;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      if (!state.quotationTyingEquipment) {
        state.quotationTyingEquipment = clone(MOCK_QUOTATION_TYING_EQUIPMENT);
      }

      const { key, value } = action.payload;
      state.quotationTyingEquipment[key] = {
        ...state.quotationTyingEquipment[key],
        ...value,
      };
    },
    addQuotationEquipmentTableRow: (
      state,
      action: PayloadAction<QuotationEquipmentTableKey>
    ) => {
      if (!state.quotationTyingEquipment) {
        state.quotationTyingEquipment = clone(MOCK_QUOTATION_TYING_EQUIPMENT);
      }

      state.quotationTyingEquipment[action.payload].push(createEmptyAddOnTableRow());
    },
    updateQuotationSafetyVehicle: (
      state,
      action: PayloadAction<{
        key: keyof QuotationSafetyVehicleState;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      if (!state.quotationSafetyVehicle) {
        state.quotationSafetyVehicle = clone(MOCK_QUOTATION_SAFETY_VEHICLE);
      }

      const { key, value } = action.payload;
      state.quotationSafetyVehicle[key] = {
        ...state.quotationSafetyVehicle[key],
        ...value,
      };
    },
    updateQuotationLocks: (
      state,
      action: PayloadAction<{
        key: keyof QuotationLocksState;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      if (!state.quotationLocks) {
        state.quotationLocks = clone(MOCK_QUOTATION_LOCKS);
      }

      const { key, value } = action.payload;
      state.quotationLocks[key] = {
        ...state.quotationLocks[key],
        ...value,
      };
    },
    updateQuotationStandard95: (
      state,
      action: PayloadAction<{
        key: keyof QuotationStandard95State;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      if (!state.quotationStandard95) {
        state.quotationStandard95 = clone(MOCK_QUOTATION_STANDARD_95);
      }

      const { key, value } = action.payload;
      state.quotationStandard95[key] = {
        ...state.quotationStandard95[key],
        ...value,
      };
    },
    resetQuotationStandard95: (state) => {
      state.quotationStandard95 = clone(MOCK_QUOTATION_STANDARD_95);
    },
    calculateQuotationPrice: (state) => {
      // Placeholder until quotation pricing API is available
      state.error = null;
    },
    updateCentreOfGravityFormField: (
      state,
      action: PayloadAction<{
        field: keyof CentreOfGravityFormValues;
        value: string;
      }>
    ) => {
      if (!state.centreOfGravityForm) {
        state.centreOfGravityForm = clone(MOCK_CENTRE_OF_GRAVITY_FORM);
      }

      const { field, value } = action.payload;
      state.centreOfGravityForm[field] = value as never;
    },
    setCentreOfGravityAdjustmentMode: (
      state,
      action: PayloadAction<CentreOfGravityAdjustmentMode>
    ) => {
      if (!state.centreOfGravityForm) {
        state.centreOfGravityForm = clone(MOCK_CENTRE_OF_GRAVITY_FORM);
      }

      state.centreOfGravityForm.adjustmentMode = action.payload;
    },
    resetCentreOfGravityForm: (state) => {
      state.centreOfGravityForm = clone(MOCK_CENTRE_OF_GRAVITY_FORM);
    },
    updateChassisLoadValue: (
      state,
      action: PayloadAction<{ key: ChasisLoad; value: string }>
    ) => {
      if (!state.chassisLoads) {
        state.chassisLoads = clone(MOCK_CHASSIS_LOADS);
      }

      const { key, value } = action.payload;
      state.chassisLoads[key] = {
        ...state.chassisLoads[key],
        value,
      };
    },
    updateVehicleMeasureValue: (
      state,
      action: PayloadAction<{ key: VehicleMeasureCode; value: string }>
    ) => {
      if (!state.vehicleMeasures) {
        state.vehicleMeasures = clone(MOCK_VEHICLE_MEASURES);
      }

      const { key, value } = action.payload;
      state.vehicleMeasures[key] = {
        ...state.vehicleMeasures[key],
        value,
      };
    },
    updateVehicleMeasureCalculationValue: (
      state,
      action: PayloadAction<{ key: VehicleMeasureCode; calculationValue: string }>
    ) => {
      if (!state.vehicleMeasures) {
        state.vehicleMeasures = clone(MOCK_VEHICLE_MEASURES);
      }

      const { key, calculationValue } = action.payload;
      state.vehicleMeasures[key] = {
        ...state.vehicleMeasures[key],
        calculationValue,
      };
    },
    updateAdditionalEquipmentValue: (
      state,
      action: PayloadAction<{ key: AdditionalEquipmentCode; value: string }>
    ) => {
      if (!state.additionalEquipment) {
        state.additionalEquipment = clone(MOCK_ADDITIONAL_EQUIPMENT);
      }

      const { key, value } = action.payload;
      state.additionalEquipment[key] = {
        ...state.additionalEquipment[key],
        value,
      };
    },
    updateAddOnFormField: (
      state,
      action: PayloadAction<{
        field: keyof AddOnFormValues;
        value: Partial<AddOnItemFormValue>;
      }>
    ) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { field, value } = action.payload;
      addOnDetails.form[field] = {
        ...addOnDetails.form[field],
        ...value,
      };
    },
    addAddOnTableRow: (state, action: PayloadAction<AddOnTableKey>) => {
      const addOnDetails = ensureAddOnDetails(state);

      addOnDetails.tables[action.payload].push(createEmptyAddOnTableRow());
    },
    addAddOnTableRowWithValues: (
      state,
      action: PayloadAction<{
        tableKey: AddOnTableKey;
        values: Partial<Omit<AddOnTableRow, "id">>;
      }>
    ) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { tableKey, values } = action.payload;
      addOnDetails.tables[tableKey].push({
        ...createEmptyAddOnTableRow(),
        ...values,
      });
    },
    updateAddOnTableRow: (
      state,
      action: PayloadAction<{
        tableKey: AddOnTableKey;
        rowId: string;
        field: keyof Omit<AddOnTableRow, "id">;
        value: string;
      }>
    ) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { tableKey, rowId, field, value } = action.payload;
      const row = addOnDetails.tables[tableKey].find((item) => item.id === rowId);

      if (!row) {
        return;
      }

      row[field] = value;
    },
    openAddOnTyingStripModal: (state, action: PayloadAction<{ titleKey: string }>) => {
      const addOnDetails = ensureAddOnDetails(state);

      addOnDetails.tyingStripModal = {
        open: true,
        titleKey: action.payload.titleKey,
        height: "",
        note: "",
      };
    },
    closeAddOnTyingStripModal: (state) => {
      const addOnDetails = ensureAddOnDetails(state);

      addOnDetails.tyingStripModal = createInitialTyingStripModal();
    },
    updateAddOnTyingStripModalField: (
      state,
      action: PayloadAction<{
        field: keyof Omit<AddOnTyingStripModalState, "open">;
        value: string;
      }>
    ) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { field, value } = action.payload;
      addOnDetails.tyingStripModal[field] = value;
    },
    submitAddOnTyingStripModal: (state) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { height, note } = addOnDetails.tyingStripModal;

      if (!height.trim()) {
        return;
      }

      addOnDetails.tables.tyingStrip.push({
        ...createEmptyAddOnTableRow(),
        height,
        note,
      });
      addOnDetails.tyingStripModal = createInitialTyingStripModal();
    },
    openRowModal: (
      state,
      action: PayloadAction<{
        tableKey: Extract<AddOnTableKey, "toolboxes" | "openBasket">;
        titleKey: string;
      }>
    ) => {
      const addOnDetails = ensureAddOnDetails(state);
      const { tableKey, titleKey } = action.payload;

      addOnDetails.rowModal = {
        open: true,
        tableKey,
        titleKey,
        location: "",
        itemType: "",
        quantity: "1",
        note: "",
      };
    },
    closeRowModal: (state) => {
      const addOnDetails = ensureAddOnDetails(state);

      addOnDetails.rowModal = createRowModal();
    },
    patchRowModal: (
      state,
      action: PayloadAction<{
        field: keyof Omit<TableRowModalState, "open" | "tableKey" | "titleKey">;
        value: string;
      }>
    ) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { field, value } = action.payload;
      addOnDetails.rowModal[field] = value;
    },
    submitRowModal: (state) => {
      const addOnDetails = ensureAddOnDetails(state);

      const { tableKey, location, itemType, quantity, note } = addOnDetails.rowModal;

      if (!tableKey || !location.trim() || !itemType) {
        return;
      }

      addOnDetails.tables[tableKey].push({
        ...createEmptyAddOnTableRow(),
        location,
        itemType,
        quantity,
        note,
      });
      addOnDetails.rowModal = createRowModal();
    },
    setDiagramDepartment: (state, action: PayloadAction<DiagramDepartmentTab>) => {
      state.diagramView.activeDepartment = action.payload;
    },
    setDiagramPage: (state, action: PayloadAction<number>) => {
      state.diagramView.activePage = action.payload;
    },
    exportDiagramPdf: (state) => {
      // Placeholder until PDF export API is available
      state.error = null;
    },
    createWorkOrderFromForm(state, action: PayloadAction<AddOrderForm>) {
      const form = action.payload;
      const nextId =
        state.workOrders.reduce((maxId, row) => Math.max(maxId, row.id), 0) + 1;

      state.workOrders.unshift({
        id: nextId,
        workOrderId: `WO-${5500 + nextId}`,
        status: WorkOrderStatus.WAITING_ENTRY,
        customerName: form.customerName || "TAVGA_TRANSPORT",
        vehicle: form.model || "CLOSED_BOX_TRUCK",
        currentStage: "METALWORK",
        progress: 0,
        priority: "NORMAL",
      });
    },
  },
});

export const { reducer, actions, name: sliceKey } = workOrderSlice;
